import React, {ReactElement, useState} from 'react';
import {useDispatch} from 'react-redux';
import Label from 'shared/components/label';
import {Formik, Form, Field} from 'formik';
import Button from 'src/components/button';
import FormikTextField from 'src/components/FormikTextField';
import Checkbox from 'shared/components/checkbox';
import FormikCheckbox from 'src/components/formik-checkbox';
import {map, startCase} from 'lodash';
import Details from '../Details';
import Testimonials from '../Testimonials';
import {Link} from 'react-router-dom';
import UniversityDropdown from 'src/views/admin/UniversityDropdown';
import styles from './styles.module.scss';
import {useBoolean} from 'src/hooks/useBoolean';
import {Box} from '@mui/material';
import EditResourcesForCategory from 'src/views/admin/EditResourcesForCategory';
import {createOrUpdateTimeSensitiveItemType} from 'src/redux/admin/actions/timeSensitiveItemTypes';

interface Values {
  sponsorId: string;
  _id?: string;
  scholarshipProgramId: string;
  educationPhase: string;
  description: string;
  detailedDescription: string;
  title: string;
  emoji: string;
  socialNote: string;
  socialNumber: number;
  completedAtText: string;
  details: never[];
  testimonials: never[];
  displayOpportunityCard: boolean;
}

const inputFields = [
  {type: 'text', name: 'title', placeholder: 'Meet with advisor', required: true},
  {type: 'textarea', name: 'description', placeholder: 'This could be helpful', required: true},
  {
    type: 'textarea',
    name: 'detailedDescription',
    placeholder: 'Studies show this could be helpful',
    required: false,
  },
  {
    type: 'text',
    name: 'completedAtText',
    placeholder: 'When did you meet with your advisor?',
    required: false,
  },
  {type: 'text', name: 'emoji', placeholder: '', required: false},
  {type: 'text', name: 'socialNote', placeholder: '', required: false},
  {type: 'text', name: 'socialNumber', placeholder: '', required: false},
];

interface TimeSensitiveItemTypeProps {
  type: Values;
}

const TimeSensitiveItemTypes = (props: TimeSensitiveItemTypeProps) => {
  const dispatch = useDispatch();

  const onSubmit = (values: Values): void => {
    dispatch(createOrUpdateTimeSensitiveItemType(values));
  };
  const globalItem = useBoolean(!!props.type._id && !props.type.sponsorId);
  // tracks the most recently selected sponsorId so if global is toggled on and off it will restore the selection
  const [previouslySelectedSponsorId, setPreviouslySelectedSponsorId] = useState(null);

  return (
    <div className={styles.container}>
      <Formik initialValues={props.type} onSubmit={onSubmit}>
        {({values, handleSubmit}): ReactElement => (
          <Form onSubmit={handleSubmit}>
            <Field name="sponsorId" type="text">
              {({field: {name, value}, form: {setFieldValue}}): ReactElement => (
                <>
                  <UniversityDropdown
                    onChange={(sponsorId): void => setFieldValue(name, sponsorId)}
                    value={value}
                    disabled={globalItem.value}
                  />
                  <Box mt={2}>
                    <Checkbox
                      onChange={(e) => {
                        if (e.target.checked) {
                          setPreviouslySelectedSponsorId(value);
                          setFieldValue(name, null);
                          globalItem.setTrue();
                        } else {
                          setFieldValue(name, previouslySelectedSponsorId);
                          globalItem.setFalse();
                        }
                      }}
                      checked={globalItem.value}
                      label="Allow this item type to be used globally, not tied to a single university"
                    />
                  </Box>
                </>
              )}
            </Field>
            <Field type="hidden" name="_id" />
            {map(inputFields, (field) => (
              <Label key={field.name}>
                {startCase(field.name)}
                <FormikTextField
                  name={`${field.name}`}
                  type={field.type}
                  selectValueOnFocus
                  required={field.required}
                  placeholder={`${field.placeholder}`}
                  aria-label={`${startCase(field.name)}`}
                />
              </Label>
            ))}
            <Details details={values.details} />
            <Testimonials testimonials={values.testimonials} />
            <FormikCheckbox name="displayOpportunityCard" label="Make opportunity card?" />
            <Button type="submit">Save</Button>
          </Form>
        )}
      </Formik>
      {props.type._id && (
        <EditResourcesForCategory category={`TimeSensitiveItemType-${props.type._id}`} />
      )}
      <Link to="/admin/tools/time-sensitive-item-types">Cancel and go back</Link>
    </div>
  );
};

TimeSensitiveItemTypes.defaultProps = {
  type: {
    _id: '',
    sponsorId: '',
    description: '',
    detailedDescription: '',
    title: '',
    emoji: '',
    socialNote: '',
    socialNumber: 0,
    completedAtText: '',
    details: [],
    testimonials: [],
  },
};

export default TimeSensitiveItemTypes;
